// All STRATEGY tab sections extracted from App.js.
// Each is a collapsible panel bound to the shared strategy state.
import { useMemo, useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { C } from "../../lib/colors";
import { fmtMoney } from "../../lib/format";
import { useT } from "../LangContext";
import {
  Collapsible, NumField, SelectField, ToggleRow, InfoTooltip,
} from "../shared/ui";
import { parseBootstrapData, INSTRUMENT_MAE_RATIOS } from "../../monteCarlo";
import { hint } from "../../lib/hints";
import { track } from "../../lib/track";
import { resolveFundedRules } from "../../firmDatabase";
import { exampleAsCSV } from "../../lib/exampleData";


// Fase 15-16 · Bloque 8c — Simple-mode presets
const SIMPLE_PRESETS = [
  { key: "preset_1", wr: 0.52, muWin: 180, sigmaWin: 90,  muLoss: -140, sigmaLoss: 60,  tailProb: 0.02, tailMult: 1.4 },
  { key: "preset_2", wr: 0.44, muWin: 320, sigmaWin: 200, muLoss: -260, sigmaLoss: 120, tailProb: 0.04, tailMult: 1.6 },
  { key: "preset_3", wr: 0.38, muWin: 420, sigmaWin: 220, muLoss: -210, sigmaLoss: 80,  tailProb: 0.03, tailMult: 1.5 },
  { key: "preset_4", wr: 0.47, muWin: 260, sigmaWin: 140, muLoss: -200, sigmaLoss: 90,  tailProb: 0.03, tailMult: 1.5 },
];
function SimplePresets({ strategy, setStrategy }) {
  const { t } = useT();
  return (
    <div data-testid="simple-presets"
         style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12,
                   alignItems: "center" }}
         title={t("preset_tooltip")}>
      <span style={{ color: C.smoke, fontFamily: "var(--mono)", fontSize: 10.5,
                      letterSpacing: 0.2, textTransform: "uppercase", marginRight: 4 }}>
        {t("preset_label")}:
      </span>
      {SIMPLE_PRESETS.map(p => (
        <button key={p.key}
                data-testid={`preset-${p.key}`}
                onClick={() => setStrategy({ ...strategy, ...{
                  wr: p.wr, muWin: p.muWin, sigmaWin: p.sigmaWin,
                  muLoss: p.muLoss, sigmaLoss: p.sigmaLoss,
                  tailProb: p.tailProb, tailMult: p.tailMult,
                }})}
                style={{ background: "transparent", border: `1px solid ${C.dust}`,
                          color: C.linen, padding: "4px 10px", cursor: "pointer",
                          fontFamily: "var(--plex)", fontSize: 11.5, letterSpacing: 0.05 }}>
          {t(p.key)}
        </button>
      ))}
    </div>
  );
}

export function ModeSelector({ strategy, setStrategy }) {
  const { t } = useT();
  const mode = strategy.mode || "bootstrap";

  const handleModeChange = (modeId) => {
    setStrategy({ ...strategy, mode: modeId });
  };

  return (
    <div className="fg-panel" style={{ padding: 16 }} data-testid="mode-selector">
      <div style={{ fontFamily: "var(--plex)", fontSize: 11, letterSpacing: 0.2,
                    textTransform: "uppercase", color: C.steel, fontWeight: 500, marginBottom: 10 }}>
        § {t("mode_title")}
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {[
          // bootstrap (data-driven) is first and badged as RECOMMENDED.
          { id: "bootstrap", label: t("mode_bootstrap"), sub: t("mode_bootstrap_sub"), recommended: true },
          { id: "simple",    label: t("mode_simple"),    sub: t("mode_simple_sub"), recommended: false },
        ].map(m => {
          const selected = mode === m.id;
          return (
            <label key={m.id} style={{ 
                     flex: 1, minWidth: 200, cursor: "pointer", padding: 12,
                     border: `1px solid ${selected ? C.cinnabar : C.dust}`,
                     background: selected ? `${C.cinnabar}10` : "transparent",
                     position: 'relative',
                   }}
                   onClick={() => handleModeChange(m.id)}
                   data-testid={`mode-${m.id}`}>
              {m.recommended && (
                <span data-testid="mode-recommended-badge" style={{
                  position: 'absolute', top: 8, right: 8,
                  fontSize: 9, fontFamily: "var(--mono)", color: "#7BC47F",
                  letterSpacing: "0.12em", padding: "2px 6px",
                  border: "1px solid #7BC47F", borderRadius: 2,
                }}>
                  ★ {t("mode_recommended")}
                </span>
              )}
              <input type="radio" name="mode" checked={selected}
                     onChange={() => {}}
                     style={{ accentColor: C.cinnabar, marginRight: 10 }} />
              <span style={{ fontFamily: "var(--plex)", fontWeight: 600, fontSize: 13,
                             color: selected ? C.cinnabar : C.bone, letterSpacing: 0.05 }}>
                {m.label}
              </span>
              {selected && (
                <span data-testid={`mode-${m.id}-check`} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  marginLeft: 8, width: 16, height: 16, borderRadius: 8,
                  background: "#7BC47F",
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                       stroke="#0E0E0E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
              )}
              <div style={{ marginTop: 6, color: C.linen, fontSize: 12, fontStyle: "italic",
                            fontFamily: "var(--plex)", fontWeight: 300, lineHeight: 1.45 }}>
                {m.sub}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function PnLDistributionSection({ strategy, setStrategy }) {
  const { t } = useT();
  const mode = strategy.mode || "bootstrap";
  return (
    <Collapsible title={t("section_pnl_v2")} testId="section-pnl"
                 defaultOpen description={t("section_pnl_desc")}>
      {mode === "simple" ? (
        <>
          {/* Fase 15-16 · Bloque 8c — Quick presets row */}
          <SimplePresets strategy={strategy} setStrategy={setStrategy} />
          <div data-testid="simple-info-panel"
               style={{
                 padding: "12px 14px",
                 marginBottom: 14,
                 border: `1px dashed ${C.brass}`,
                 background: `${C.brass}0A`,
                 fontFamily: "var(--plex)", fontSize: 12.5, lineHeight: 1.55,
                 color: C.linen,
               }}>
            <div style={{ color: C.brass, fontWeight: 600, fontSize: 10.5,
                          letterSpacing: 0.18, textTransform: "uppercase",
                          marginBottom: 6 }}>
              ★ {t("simple_info_title")}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>
              {t("simple_info_body")}
            </div>
          </div>
          <NumField label={t("field_wr")} value={strategy.wr} step={0.01} tip="wr"
                    onChange={v => setStrategy({ ...strategy, wr: v })} testId="strat-wr" />
          <NumField label={t("field_mu_win")} prefix="$" value={strategy.muWin} tip="mu_sigma"
                    onChange={v => setStrategy({ ...strategy, muWin: v })} testId="strat-muwin" />
          <NumField label={t("field_sigma_win")} prefix="$" value={strategy.sigmaWin}
                    onChange={v => setStrategy({ ...strategy, sigmaWin: v })} testId="strat-sigmawin" />
          <NumField label={t("field_mu_loss")} prefix="$" value={strategy.muLoss}
                    onChange={v => setStrategy({ ...strategy, muLoss: v })} testId="strat-muloss" />
          <NumField label={t("field_sigma_loss")} prefix="$" value={strategy.sigmaLoss}
                    onChange={v => setStrategy({ ...strategy, sigmaLoss: v })} testId="strat-sigmaloss" />
          <NumField label={t("field_tail_prob")} value={strategy.tailProb} step={0.001} tip="spike"
                    onChange={v => setStrategy({ ...strategy, tailProb: v })} testId="strat-tailprob" />
          <NumField label={t("field_tail_mult")} value={strategy.tailMult} step={0.01}
                    onChange={v => setStrategy({ ...strategy, tailMult: v })} testId="strat-tailmult" />
        </>
      ) : (
        <BootstrapInput strategy={strategy} setStrategy={setStrategy} />
      )}
    </Collapsible>
  );
}

export function BootstrapInput({ strategy, setStrategy }) {
  const { t } = useT();
  const [raw, setRaw] = useState("");
  const [errors, setErrors] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Column assignments inside the Excel/CSV column-picker modal.
  const [pnlCol, setPnlCol] = useState(null);
  const [maeCol, setMaeCol] = useState(null);
  const [mfeCol, setMfeCol] = useState(null);
  const fileInputRef = useRef(null);
  const parseTimerRef = useRef(null);
  const stats = strategy.bootstrapStats;

  const parse = (text, source = "paste") => {
    const { data, stats: s, errors: errs } = parseBootstrapData(text);
    setErrors(errs || []);
    setStrategy({ ...strategy, bootstrapData: data, bootstrapStats: s });
    if (data && data.length > 0) {
      track("data_loaded", { source, n: data.length, tradeLevel: !!s?.tradeLevel });
    }
    // Fase 15-16 · Bloque 4 hint #2 — valid bootstrap data loaded
    if (data && data.length >= 30) {
      hint("data_loaded", {
        title: t("hint_data_loaded", { n: data.length }),
        action: { label: t("hint_go"), onClick: () => {
          // Trigger nav by dispatching a custom event the App listens to
          window.dispatchEvent(new CustomEvent("pf:nav", { detail: { to: "lab" } }));
        }},
      });
    }
  };

  // Auto-parse with 250ms debounce so typing/pasting feels instant but doesn't
  // spam the parser on every keystroke.
  const setRawAndParse = (text) => {
    setRaw(text);
    if (parseTimerRef.current) clearTimeout(parseTimerRef.current);
    if (!text.trim()) {
      setErrors([]);
      setStrategy({ ...strategy, bootstrapData: [], bootstrapStats: null });
      return;
    }
    parseTimerRef.current = setTimeout(() => parse(text), 250);
  };

  const pasteClip = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setRaw(text); parse(text);
    } catch { setErrors(["Clipboard access denied"]); }
  };

  const loadExample = () => {
    const text = exampleAsCSV();
    setRaw(text);
    parse(text);
  };

  const handleClear = () => {
    if (parseTimerRef.current) clearTimeout(parseTimerRef.current);
    setRaw("");
    setErrors([]);
    setStrategy({ ...strategy, bootstrapData: [], bootstrapStats: null });
  };

  // ─── File upload (Excel + CSV) ──────────────────────────────────────────
  const resetColumnState = () => { setPnlCol(null); setMaeCol(null); setMfeCol(null); };

  const readFile = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setErrors([t("bootstrap_error_format")]);
      return;
    }
    setErrors([]);
    setIsProcessing(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        // SheetJS reads CSV via the same XLSX.read() with type 'array'.
        const buf = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(buf, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        loadSheetIntoSelector(workbook, firstSheetName, file.name);
      } catch (err) {
        console.error(err);
        setErrors([t("bootstrap_error_parse")]);
      } finally {
        setIsProcessing(false);
      }
    };
    reader.onerror = () => {
      setIsProcessing(false);
      setErrors([t("bootstrap_error_parse")]);
    };
    reader.readAsArrayBuffer(file);
  };

  const loadSheetIntoSelector = (workbook, sheetName, fileName) => {
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1, defval: null, blankrows: false,
    });
    if (rows.length < 2) {
      setErrors([t("bootstrap_error_empty")]);
      return;
    }
    const headers = rows[0] || [];
    const colCount = Math.max(headers.length,
      ...rows.slice(1, 11).map(r => (r ? r.length : 0)));
    const padRow = (r) => Array.from({ length: colCount }, (_, i) =>
      (r && r[i] !== undefined ? r[i] : null));

    // Heuristics: try to auto-detect P&L / MAE / MFE columns from header text.
    const lcHeaders = headers.map(h => String(h ?? "").toLowerCase());
    const findIdx = (aliases) => lcHeaders.findIndex(h =>
      aliases.some(a => h === a || h.includes(a)));
    const guessPnl = findIdx(["pnl", "p&l", "p/l", "profit", "net", "result"]);
    const guessMae = findIdx(["mae", "adverse", "drawdown", "max_dd"]);
    const guessMfe = findIdx(["mfe", "favorable", "favourable", "max_up"]);

    setExcelData({
      workbook,
      sheetNames: workbook.SheetNames,
      sheetName,
      fileName,
      headers: Array.from({ length: colCount }, (_, i) => headers[i]),
      rows: rows.slice(1, 11).map(padRow),
      allRows: rows.slice(1).map(padRow),
    });
    setPnlCol(guessPnl >= 0 ? guessPnl : null);
    setMaeCol(guessMae >= 0 ? guessMae : null);
    setMfeCol(guessMfe >= 0 ? guessMfe : null);
  };

  const switchSheet = (newSheetName) => {
    if (!excelData) return;
    loadSheetIntoSelector(excelData.workbook, newSheetName, excelData.fileName);
  };

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) readFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };

  const cleanNumber = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const cleaned = String(v).replace(/[^\d,.\-+eE]/g, "").replace(",", ".");
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const confirmColumnSelection = () => {
    if (!excelData || pnlCol === null) return;

    const headers = ["pnl"];
    const indices = [pnlCol];
    if (maeCol !== null) { headers.push("mae"); indices.push(maeCol); }
    if (mfeCol !== null) { headers.push("mfe"); indices.push(mfeCol); }

    const lines = [headers.join(",")];
    for (const row of excelData.allRows) {
      const values = indices.map(i => cleanNumber(row[i]));
      // Skip rows whose P&L value is missing/non-numeric.
      if (values[0] === null) continue;
      lines.push(values.map(v => v === null ? "" : v).join(","));
    }

    const numericRowCount = lines.length - 1;
    if (numericRowCount < 30) {
      setErrors([t("bootstrap_excel_too_few_values", { count: numericRowCount })]);
      return;
    }

    const csvText = lines.join("\n");
    setRaw(csvText);
    parse(csvText);
    setExcelData(null);
    resetColumnState();
    setErrors([]);
  };

  const cancelExcelImport = () => {
    setExcelData(null);
    resetColumnState();
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div data-testid="bootstrap-input">
      <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                    fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}>
        {t("bootstrap_help")}
      </div>

      {/* Drag & drop wrapper */}
      <div onDragOver={handleDragOver}
           onDragLeave={handleDragLeave}
           onDrop={handleDrop}
           style={{ position: "relative" }}>
        <textarea
          className="fg-input"
          style={{ minHeight: 140, fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.5 }}
          placeholder={t("bootstrap_placeholder")}
          value={raw}
          onChange={(e) => setRawAndParse(e.target.value)}
          data-testid="bootstrap-textarea"
        />
        {isDragging && (
          <div data-testid="bootstrap-drop-overlay"
               style={{
                 position: "absolute", inset: 0,
                 background: `${C.cinnabar}26`,
                 border: `2px dashed ${C.cinnabar}`,
                 display: "flex", alignItems: "center", justifyContent: "center",
                 fontFamily: "var(--plex)", fontSize: 14, fontWeight: 600,
                 color: C.bone, letterSpacing: "0.05em",
                 pointerEvents: "none",
               }}>
            ↓ {t("bootstrap_drop_here")}
          </div>
        )}
        {isProcessing && (
          <div data-testid="bootstrap-processing"
               style={{
                 position: "absolute", inset: 0,
                 background: `${C.ink}cc`,
                 display: "flex", alignItems: "center", justifyContent: "center",
                 fontFamily: "var(--mono)", fontSize: 12, color: C.brass,
                 letterSpacing: "0.1em",
                 pointerEvents: "none",
               }}>
            <span className="bootstrap-spinner" style={{
              width: 14, height: 14, marginRight: 10,
              border: `2px solid ${C.brass}`, borderTopColor: "transparent",
              borderRadius: "50%", display: "inline-block",
              animation: "bootstrap-spin 0.7s linear infinite",
            }} />
            {t("bootstrap_loading")}
          </div>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv"
             onChange={onFileInputChange}
             style={{ display: "none" }}
             data-testid="bootstrap-file-input" />

      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button className="fg-btn-ghost" onClick={pasteClip} data-testid="bootstrap-paste"
                style={{ flex: 1, minWidth: 120 }}>
          {t("bootstrap_paste_clip")}
        </button>
        <button className="fg-btn-ghost" onClick={loadExample} data-testid="bootstrap-load-example">
          {t("bootstrap_load_example")}
        </button>
        <button className="fg-btn-ghost"
                onClick={() => fileInputRef.current?.click()}
                data-testid="bootstrap-upload-file">
          {t("bootstrap_upload_file")}
        </button>
        <button className="fg-btn-ghost" onClick={handleClear}
                disabled={!raw && !stats}
                data-testid="bootstrap-clear"
                style={{ opacity: (!raw && !stats) ? 0.5 : 1 }}>
          {t("bootstrap_clear")}
        </button>
      </div>

      {/* Column-assignment modal */}
      {excelData && (
        <div data-testid="excel-column-modal"
             onClick={(e) => { if (e.target === e.currentTarget) cancelExcelImport(); }}
             style={{
               position: "fixed", inset: 0, zIndex: 100,
               background: `${C.ink}d9`,
               display: "flex", alignItems: "center", justifyContent: "center",
               padding: 24,
             }}>
          <div style={{
            width: "min(960px, 100%)", maxHeight: "92vh",
            background: C.archive, border: `1px solid ${C.dust}`,
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.dust}` }}>
              <div style={{ fontFamily: "var(--plex)", fontSize: 12, letterSpacing: 0.18,
                            textTransform: "uppercase", color: C.brass, fontWeight: 600 }}>
                § {t("bootstrap_excel_select_columns_title")}
              </div>
              <div style={{ marginTop: 6, fontFamily: "var(--mono)", fontSize: 11,
                            color: C.smoke, display: "flex", gap: 12,
                            alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: C.bone }}>{excelData.fileName}</span>
                <span style={{ color: C.haze }}>·</span>
                <span>{t("bootstrap_excel_sheet")}:</span>
                {excelData.sheetNames.length > 1 ? (
                  <select className="fg-select"
                          data-testid="excel-sheet-select"
                          value={excelData.sheetName}
                          onChange={(e) => switchSheet(e.target.value)}
                          style={{ padding: "4px 8px", fontSize: 11 }}>
                    {excelData.sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                ) : (
                  <span style={{ color: C.bone }}>{excelData.sheetName}</span>
                )}
              </div>
            </div>

            <div style={{ padding: 18, overflowY: "auto" }}>
              {/* 3 column-assignment dropdowns */}
              <div style={{ display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: 14, marginBottom: 18 }}>
                {[
                  { key: "pnl", value: pnlCol, setter: setPnlCol, required: true,
                    label: t("bootstrap_excel_label_pnl"), help: t("bootstrap_excel_help_pnl") },
                  { key: "mae", value: maeCol, setter: setMaeCol, required: false,
                    label: t("bootstrap_excel_label_mae"), help: t("bootstrap_excel_help_mae") },
                  { key: "mfe", value: mfeCol, setter: setMfeCol, required: false,
                    label: t("bootstrap_excel_label_mfe"), help: t("bootstrap_excel_help_mfe") },
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: "block", fontFamily: "var(--plex)",
                                    fontSize: 11.5, letterSpacing: 0.05,
                                    color: field.required ? C.cinnabar : C.bone,
                                    fontWeight: 600, marginBottom: 4,
                                    textTransform: "uppercase" }}>
                      {field.label}
                      {!field.required && (
                        <span style={{ marginLeft: 6, color: C.smoke, fontWeight: 400,
                                       textTransform: "lowercase", fontStyle: "italic" }}>
                          ({t("optional")})
                        </span>
                      )}
                    </label>
                    <select
                      data-testid={`excel-col-${field.key}`}
                      className="fg-select"
                      value={field.value === null ? "" : String(field.value)}
                      onChange={(e) => {
                        const v = e.target.value;
                        field.setter(v === "" ? null : Number(v));
                      }}
                      style={{ width: "100%", padding: "8px 10px", fontSize: 12 }}>
                      <option value="">
                        {field.required
                          ? t("bootstrap_excel_select_placeholder")
                          : t("bootstrap_excel_select_none")}
                      </option>
                      {excelData.headers.map((h, i) => (
                        <option key={i} value={i}>
                          {h
                            ? String(h)
                            : `${t("bootstrap_excel_column")} ${String.fromCharCode(65 + i)}`}
                        </option>
                      ))}
                    </select>
                    <div style={{ marginTop: 4, fontFamily: "var(--plex)",
                                  fontSize: 11, fontStyle: "italic",
                                  color: C.smoke, lineHeight: 1.5 }}>
                      {field.help}
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview */}
              <div style={{ fontFamily: "var(--plex)", fontSize: 11, letterSpacing: 0.18,
                            textTransform: "uppercase", color: C.steel,
                            fontWeight: 600, marginBottom: 6 }}>
                § {t("bootstrap_excel_preview")}
                <span style={{ marginLeft: 8, color: C.smoke,
                               fontWeight: 400, textTransform: "lowercase",
                               fontStyle: "italic" }}>
                  ({Math.min(excelData.rows.length, 10)} {t("bootstrap_excel_first_rows")})
                </span>
              </div>
              <div style={{ overflowX: "auto", maxWidth: "100%",
                            border: `1px solid ${C.haze}` }}>
                <table style={{
                  borderCollapse: "collapse", fontFamily: "var(--mono)",
                  fontSize: 11.5, minWidth: "100%",
                }}>
                  <thead>
                    <tr>
                      {excelData.headers.map((header, colIdx) => {
                        const role = pnlCol === colIdx ? "pnl"
                                   : maeCol === colIdx ? "mae"
                                   : mfeCol === colIdx ? "mfe" : null;
                        const roleColor = role === "pnl" ? C.cinnabar
                                        : role === "mae" ? C.brass
                                        : role === "mfe" ? "#7BC47F"
                                        : C.smoke;
                        return (
                          <th key={colIdx} style={{
                            padding: "8px 10px",
                            borderRight: `1px solid ${C.haze}`,
                            borderBottom: `1px solid ${C.haze}`,
                            verticalAlign: "top", minWidth: 130,
                            background: role ? `${roleColor}1A` : C.archive,
                            textAlign: "left",
                            color: role ? roleColor : C.bone,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}>
                            {role && (
                              <span style={{ fontSize: 9, letterSpacing: "0.12em",
                                             marginRight: 6,
                                             padding: "1px 5px",
                                             border: `1px solid ${roleColor}`,
                                             color: roleColor }}>
                                {role.toUpperCase()}
                              </span>
                            )}
                            {header
                              ? String(header)
                              : `${t("bootstrap_excel_column")} ${String.fromCharCode(65 + colIdx)}`}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {excelData.rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {excelData.headers.map((_, colIdx) => {
                          const role = pnlCol === colIdx ? "pnl"
                                     : maeCol === colIdx ? "mae"
                                     : mfeCol === colIdx ? "mfe" : null;
                          const roleColor = role === "pnl" ? C.cinnabar
                                          : role === "mae" ? C.brass
                                          : role === "mfe" ? "#7BC47F"
                                          : null;
                          return (
                            <td key={colIdx} style={{
                              padding: "5px 10px",
                              borderRight: `1px solid ${C.haze}`,
                              borderBottom: `1px solid ${C.haze}`,
                              color: role ? roleColor : C.linen,
                              background: role ? `${roleColor}0D` : "transparent",
                              whiteSpace: "nowrap",
                            }}>
                              {row[colIdx] !== null && row[colIdx] !== undefined && row[colIdx] !== ""
                                ? String(row[colIdx])
                                : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.dust}`,
                          display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={cancelExcelImport}
                      data-testid="excel-cancel"
                      className="fg-btn-ghost"
                      style={{ padding: "8px 18px" }}>
                {t("bootstrap_excel_cancel")}
              </button>
              <button
                onClick={confirmColumnSelection}
                disabled={pnlCol === null}
                data-testid="excel-confirm"
                style={{
                  padding: "8px 18px",
                  background: pnlCol === null ? C.dust : C.cinnabar,
                  color: pnlCol === null ? C.smoke : C.bone,
                  border: "none",
                  fontFamily: "var(--mono)", fontSize: 11,
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  fontWeight: 600,
                  cursor: pnlCol === null ? "not-allowed" : "pointer",
                }}>
                {t("bootstrap_excel_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div style={{ marginTop: 10, color: C.cinnabar, fontSize: 12, fontStyle: "italic",
                      fontFamily: "var(--plex)" }}>
          {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}

      {stats && (
        <div style={{ marginTop: 14, padding: 14, background: C.leather, border: `1px solid ${C.dust}` }}
             data-testid="bootstrap-summary">
          <div style={{ fontFamily: "var(--plex)", fontSize: 11, letterSpacing: 0.2,
                        textTransform: "uppercase", color: C.steel, marginBottom: 10 }}>
            § {t("bootstrap_summary_title")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontFamily: "var(--mono)", fontSize: 12.5 }}>
            <div><span style={{ color: C.smoke }}>total days:</span> <span style={{ color: C.bone }}>{stats.total}</span></div>
            <div><span style={{ color: C.smoke }}>WR:</span> <span style={{ color: C.bone }}>{(stats.wr * 100).toFixed(1)}%</span></div>
            <div><span style={{ color: C.smoke }}>wins:</span> <span style={{ color: C.bone }}>{stats.wins}</span></div>
            <div><span style={{ color: C.smoke }}>losses:</span> <span style={{ color: C.bone }}>{stats.losses}</span></div>
            <div><span style={{ color: C.smoke }}>μ win:</span> <span style={{ color: C.bone }}>{stats.meanWin.toFixed(0)}</span></div>
            <div><span style={{ color: C.smoke }}>μ loss:</span> <span style={{ color: C.bone }}>{stats.meanLoss.toFixed(0)}</span></div>
            <div><span style={{ color: C.smoke }}>best:</span> <span style={{ color: C.bone }}>+{stats.best.toFixed(0)}</span></div>
            <div><span style={{ color: C.smoke }}>worst:</span> <span style={{ color: C.bone }}>{stats.worst.toFixed(0)}</span></div>
            <div><span style={{ color: C.smoke }}>MAE column:</span>
              <span style={{ color: stats.hasMae ? C.bone : C.cinnabar }}> {stats.hasMae ? "yes" : "no"}</span>
            </div>
            <div><span style={{ color: C.smoke }}>autocorr:</span>
              <span style={{ color: Math.abs(stats.autocorrelation) > 0.2 ? C.cinnabar : C.bone }}>
                {" "}{stats.autocorrelation.toFixed(2)}
              </span>
            </div>
          </div>
          {stats.total >= 100
            ? <div style={{ marginTop: 8, color: C.brass, fontSize: 11.5, fontFamily: "var(--plex)" }}>
                ✓ {stats.total} samples (recommended ≥100)
              </div>
            : <div style={{ marginTop: 8, color: C.cinnabar, fontSize: 11.5, fontFamily: "var(--plex)" }}>
                ⚠ {t("bootstrap_min_warning")}
              </div>}

          {/* Fase 15-16 · Bloque 8a — trade-level aggregation notice */}
          {stats.tradeLevel && (
            <div data-testid="bootstrap-trade-level-notice"
                 style={{ marginTop: 8, color: C.brass, fontFamily: "var(--mono)",
                          fontSize: 11.5, letterSpacing: 0.1 }}>
              ✦ {t("boot_trade_level", { nTrades: stats.nTrades, nDays: stats.nDays })}
            </div>
          )}

          {/* FIX 2b — Preview of first parsed values (helps spot decimal-comma mis-reads). */}
          {stats.previewValues && stats.previewValues.length > 0 && (
            <div data-testid="bootstrap-preview-values"
                 style={{ marginTop: 10, fontFamily: "var(--mono)", fontSize: 11.5,
                          color: C.smoke, lineHeight: 1.55 }}>
              {t("boot_preview_values", {
                values: stats.previewValues
                  .map(v => {
                    const isInt = Number.isInteger(v);
                    return isInt ? v.toString() : v.toFixed(2);
                  })
                  .join(", "),
              })}
            </div>
          )}

          {/* FIX 1c — Warning when bootstrap data has no MAE column. */}
          {stats.hasMae === false && (
            <div data-testid="bootstrap-no-mae-warning"
                 style={{
                   marginTop: 10, padding: "8px 12px",
                   border: `1px solid ${C.brass}`,
                   background: `${C.brass}14`,
                   color: C.linen, fontFamily: "var(--plex)",
                   fontSize: 11.5, lineHeight: 1.5, fontStyle: "italic",
                 }}>
              ⚠ {t("boot_no_mae_warning")}
            </div>
          )}

          {/* Fase 14 · Bloque 3 — block bootstrap notice + advanced toggle */}
          {stats.blockLen > 1 && (
            <div data-testid="block-sampling-notice"
                 style={{ marginTop: 10, padding: "8px 12px",
                          border: `1px solid ${C.dust}`, background: "rgba(42, 48, 50, 0.35)",
                          color: C.linen, fontFamily: "var(--plex)",
                          fontSize: 11.5, lineHeight: 1.5, display: "flex",
                          justifyContent: "space-between", alignItems: "center",
                          gap: 12, flexWrap: "wrap" }}>
              <span>✦ {t("block_sampling_notice", { L: stats.blockLen })}</span>
              <label data-testid="block-sampling-toggle"
                     style={{ color: C.smoke, fontSize: 10.5, letterSpacing: 0.1,
                              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox"
                       checked={strategy.blockSampling === false}
                       onChange={(e) => setStrategy({
                         ...strategy,
                         blockSampling: e.target.checked ? false : true,
                       })}
                       style={{ accentColor: C.brass }} />
                {t("block_sampling_force_iid")}
              </label>
            </div>
          )}

          {/* Fase 16B · Bloque 1 — Export guides accordion */}
          <ExportGuides openProp={strategy._openExportGuides} />
        </div>
      )}
    </div>
  );
}

// ─── Fase 16B · Bloque 1 — Export guides accordion ────────────────────────
// Inline accordion inside the bootstrap uploader. 5 broker guides.
const EXPORT_BROKERS = ["tradovate", "ninjatrader", "metatrader", "tradingview", "other"];
function ExportGuides({ openProp }) {
  const { t } = useT();
  const [open, setOpen] = useState(!!openProp);
  const [openKey, setOpenKey] = useState(null);

  // ?from=wizard query → opens accordion automatically (Block 2 hook)
  useEffect(() => {
    if (openProp) setOpen(true);
  }, [openProp]);

  return (
    <div data-testid="export-guides-wrap" style={{ marginTop: 14 }}>
      <button data-testid="export-guides-toggle"
              onClick={() => setOpen(o => !o)}
              style={{ background: "transparent", border: `1px dashed ${C.dust}`,
                        color: C.brass, padding: "8px 12px", cursor: "pointer",
                        fontFamily: "var(--mono)", fontSize: 11, letterSpacing: 0.18,
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", width: "100%" }}>
        <span>{open ? "▾" : "▸"}  {t("exp__title")}</span>
        <span style={{ color: C.smoke, fontSize: 10 }}>{EXPORT_BROKERS.length}</span>
      </button>
      {open && (
        <div data-testid="export-guides-list"
             style={{ marginTop: 8, border: `1px solid ${C.dust}`,
                       background: "rgba(42, 48, 50, 0.30)" }}>
          {EXPORT_BROKERS.map(key => {
            const expanded = openKey === key;
            let steps = [];
            try {
              const raw = t(`exp__${key}__steps`);
              steps = typeof raw === "string" ? JSON.parse(raw) : raw;
            } catch { steps = []; }
            return (
              <div key={key} style={{ borderBottom: `1px solid ${C.haze}` }}>
                <button data-testid={`exp-${key}-toggle`}
                        onClick={() => setOpenKey(expanded ? null : key)}
                        style={{ width: "100%", textAlign: "left", padding: "10px 14px",
                                  background: "transparent", border: "none",
                                  color: expanded ? C.bone : C.linen, cursor: "pointer",
                                  fontFamily: "var(--plex)", fontSize: 12.5,
                                  letterSpacing: 0.05,
                                  display: "flex", justifyContent: "space-between" }}>
                  <span>{expanded ? "▾" : "▸"}  {t(`exp__${key}__name`)}</span>
                </button>
                {expanded && Array.isArray(steps) && (
                  <ol data-testid={`exp-${key}-steps`}
                      style={{ margin: 0, padding: "0 24px 14px 36px", color: C.linen,
                                fontFamily: "var(--plex)", fontSize: 12.5, lineHeight: 1.7 }}>
                    {steps.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function IntradayRiskSection({ strategy, setStrategy }) {
  const { t } = useT();
  const maeMode = strategy.maeMode || "estimate";
  const hasBootMae = strategy.mode === "bootstrap" && strategy.bootstrapStats?.hasMae;
  return (
    <Collapsible title={t("section_mae_v2")} testId="section-mae"
                 description={t("section_mae_desc")}
                 badge={t("section_badge_advanced")} badgeColor={C.dust}>
      <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                    fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
        {t("mae_help")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {[
          { id: "estimate", label: t("mae_estimate") },
          { id: "manual",   label: t("mae_manual") },
          { id: "auto",     label: t("mae_auto"), disabled: !hasBootMae },
        ].map(m => (
          <label key={m.id} style={{ cursor: m.disabled ? "not-allowed" : "pointer",
                                      opacity: m.disabled ? 0.4 : 1, display: "flex", alignItems: "center", gap: 8 }}
                 data-testid={`mae-mode-${m.id}`}>
            <input type="radio" name="mae-mode" checked={maeMode === m.id}
                   disabled={m.disabled}
                   onChange={() => setStrategy({ ...strategy, maeMode: m.id })}
                   style={{ accentColor: C.cinnabar }} />
            <span style={{ fontFamily: "var(--plex)", fontSize: 12.5,
                           color: maeMode === m.id ? C.cinnabar : C.bone }}>
              {m.label}
            </span>
          </label>
        ))}
      </div>
      {maeMode === "estimate" && (
        <div>
          <label className="fg-label" style={{ marginBottom: 6, display: "inline-flex", alignItems: "center" }}>
            {t("field_instrument")}
            <InfoTooltip id="instrument" />
          </label>
          <select className="fg-select" value={strategy.instrument || "nq"}
                  onChange={(e) => setStrategy({ ...strategy, instrument: e.target.value })}
                  data-testid="strat-instrument">
            {Object.entries(INSTRUMENT_MAE_RATIOS).map(([k, v]) =>
              <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div style={{ marginTop: 10, padding: 10, background: C.leather, border: `1px solid ${C.dust}`,
                        fontFamily: "var(--mono)", fontSize: 11.5, color: C.linen }}>
            {(() => {
              const r = INSTRUMENT_MAE_RATIOS[strategy.instrument || "nq"] || INSTRUMENT_MAE_RATIOS.nq;
              return (<>
                <div>win-day MAE ≈ σwin × {r.winScale.toFixed(2)} = {(strategy.sigmaWin * r.winScale).toFixed(0)}</div>
                <div>loss-day MAE ≈ |loss| × {r.lossRatio.toFixed(1)} = {(Math.abs(strategy.muLoss) * r.lossRatio).toFixed(0)}</div>
              </>);
            })()}
          </div>
        </div>
      )}
      {maeMode === "manual" && (
        <div>
          <NumField label={t("field_mae_win_mean")}  prefix="$" value={strategy.maeWinMean ?? 0}
                    onChange={v => setStrategy({ ...strategy, maeWinMean: v })}   testId="strat-maewinmean" />
          <NumField label={t("field_mae_win_std")}   prefix="$" value={strategy.maeWinStd ?? 0}
                    onChange={v => setStrategy({ ...strategy, maeWinStd: v })}    testId="strat-maewinstd" />
          <NumField label={t("field_mae_loss_mean")} prefix="$" value={strategy.maeLossMean ?? 0}
                    onChange={v => setStrategy({ ...strategy, maeLossMean: v })}  testId="strat-maelossmean" />
          <NumField label={t("field_mae_loss_std")}  prefix="$" value={strategy.maeLossStd ?? 0}
                    onChange={v => setStrategy({ ...strategy, maeLossStd: v })}   testId="strat-maelossstd" />
        </div>
      )}
      {maeMode === "auto" && (
        <div style={{ color: C.brass, fontFamily: "var(--plex)", fontSize: 12.5, fontStyle: "italic" }}>
          ✓ {t("mae_auto_ok")}
        </div>
      )}
    </Collapsible>
  );
}

export function CostsSection({ strategy, setStrategy }) {
  const { t } = useT();
  const cm = strategy.commissionMode || "none";
  const daily = cm === "estimate"
    ? (strategy.commissionPerRT || 0) * (strategy.tradesPerDay || 0) * (strategy.contractsPerTrade || 1)
    : cm === "fixed" ? (strategy.dailyCommission || 0) : 0;
  return (
    <Collapsible title={t("section_costs")} testId="section-costs"
                 description={t("section_costs_desc")}
                 badge={daily > 0 ? `−$${daily.toFixed(0)}/day` : t("section_badge_pro")}
                 badgeColor={daily > 0 ? C.cinnabar : C.dust}>
      <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                    fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
        {t("costs_help")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        {[
          { id: "none",     label: t("costs_none") },
          { id: "estimate", label: t("costs_estimate") },
          { id: "fixed",    label: t("costs_fixed") },
        ].map(m => (
          <label key={m.id} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
                 data-testid={`costs-mode-${m.id}`}>
            <input type="radio" name="costs-mode" checked={cm === m.id}
                   onChange={() => setStrategy({ ...strategy, commissionMode: m.id })}
                   style={{ accentColor: C.cinnabar }} />
            <span style={{ fontFamily: "var(--plex)", fontSize: 12.5,
                           color: cm === m.id ? C.cinnabar : C.bone }}>{m.label}</span>
          </label>
        ))}
      </div>
      {cm === "estimate" && (
        <div>
          <NumField label={t("field_comm_per_rt")} prefix="$" value={strategy.commissionPerRT}
                    onChange={v => setStrategy({ ...strategy, commissionPerRT: v })}
                    testId="strat-commrt" step={0.1} />
          <NumField label={t("field_trades_day")} value={strategy.tradesPerDay}
                    onChange={v => setStrategy({ ...strategy, tradesPerDay: v })}
                    testId="strat-tradesday" step={1} />
          <NumField label={t("field_contracts_trade")} value={strategy.contractsPerTrade}
                    onChange={v => setStrategy({ ...strategy, contractsPerTrade: v })}
                    testId="strat-contracts" step={1} />
          <div style={{ marginTop: 10, padding: 10, background: C.leather, border: `1px solid ${C.dust}`,
                        fontFamily: "var(--mono)", fontSize: 12, color: C.cinnabar }}>
            → daily cost: −${daily.toFixed(2)}
          </div>
        </div>
      )}
      {cm === "fixed" && (
        <NumField label={t("field_daily_commission")} prefix="$" value={strategy.dailyCommission}
                  onChange={v => setStrategy({ ...strategy, dailyCommission: v })}
                  testId="strat-dailycomm" step={1} />
      )}
    </Collapsible>
  );
}

export function BehavioralSection({ strategy, setStrategy }) {
  const { t } = useT();
  const postMode = strategy.postTargetMode || "conservative";
  const minDaysType = strategy.minDaysType || "total";
  const maxDaysType = strategy.maxDaysType || "trading";
  const Radio = ({ name, options, value, onChange, testPrefix }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
      {options.map(o => (
        <label key={o.id} style={{ cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 8 }}
               data-testid={`${testPrefix}-${o.id}`}>
          <input type="radio" name={name} checked={value === o.id}
                 onChange={() => onChange(o.id)}
                 style={{ accentColor: C.cinnabar, marginTop: 3 }} />
          <div>
            <div style={{ fontFamily: "var(--plex)", fontSize: 12.5,
                          color: value === o.id ? C.cinnabar : C.bone, fontWeight: 500 }}>
              {o.label}
            </div>
            {o.sub && <div style={{ fontFamily: "var(--plex)", fontStyle: "italic",
                                     color: C.linen, fontSize: 11.5, fontWeight: 300, marginTop: 2 }}>
              {o.sub}
            </div>}
          </div>
        </label>
      ))}
    </div>
  );
  return (
    <Collapsible title={t("section_behavioral")} testId="section-behavioral"
                 description={t("section_behavioral_desc")}
                 badge={t("section_badge_pro")} badgeColor={C.dust}>
      <div className="fg-label" style={{ marginBottom: 8 }}>{t("behav_post_title")}</div>
      <Radio name="post-mode" testPrefix="behav-post"
             value={postMode} onChange={v => setStrategy({ ...strategy, postTargetMode: v })}
             options={[
               { id: "conservative", label: t("behav_conservative"),  sub: t("behav_conservative_sub") },
               { id: "aggressive",   label: t("behav_aggressive"),    sub: t("behav_aggressive_sub") },
             ]} />
      <div style={{ borderTop: `1px dotted ${C.haze}`, margin: "10px 0" }} />
      <div className="fg-label" style={{ marginBottom: 8 }}>{t("behav_mindays_title")}</div>
      <Radio name="mindays-type" testPrefix="behav-mindays"
             value={minDaysType} onChange={v => setStrategy({ ...strategy, minDaysType: v })}
             options={[
               { id: "total",   label: t("behav_mindays_total") },
               { id: "winning", label: t("behav_mindays_winning") },
             ]} />
      {minDaysType === "winning" && (
        <NumField label={t("field_win_day_threshold")} prefix="$" value={strategy.winDayThreshold}
                  onChange={v => setStrategy({ ...strategy, winDayThreshold: v })}
                  testId="strat-windaythresh" />
      )}
      <div style={{ borderTop: `1px dotted ${C.haze}`, margin: "10px 0" }} />
      <div className="fg-label" style={{ marginBottom: 8 }}>{t("behav_maxdays_title")}</div>
      <Radio name="maxdays-type" testPrefix="behav-maxdays"
             value={maxDaysType} onChange={v => setStrategy({ ...strategy, maxDaysType: v })}
             options={[
               { id: "trading",  label: t("behav_maxdays_trading") },
               { id: "calendar", label: t("behav_maxdays_calendar") },
             ]} />
    </Collapsible>
  );
}

// ─────────────────────────── Post-PASS (funded cycle) section ───────────────────────────
export function PostPassSection({ strategy, setStrategy, firmId, plan }) {
  const { t } = useT();
  const enabled = !!strategy.postPassEnabled;
  const horizon = strategy.postPassHorizonMonths ?? 6;
  const sizeMode = strategy.postPassSizeMode || "same";
  const sizeFactor = strategy.postPassSizeFactor ?? 0.5;
  const ov = useMemo(() => strategy.fundedOverride || {}, [strategy.fundedOverride]);

  // Effective resolved rules (firm defaults + override) for display
  const effective = useMemo(() => {
    if (!plan) return null;
    try { return resolveFundedRules(plan, firmId, ov); } catch { return null; }
  }, [plan, firmId, ov]);

  const setOv = (patch) =>
    setStrategy({ ...strategy, fundedOverride: { ...(strategy.fundedOverride || {}), ...patch } });

  const resetDefaults = () =>
    setStrategy({ ...strategy, fundedOverride: {} });

  const Radio = ({ name, options, value, onChange, testPrefix }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
      {options.map(o => (
        <label key={o.id} style={{ cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 8 }}
               data-testid={`${testPrefix}-${o.id}`}>
          <input type="radio" name={name} checked={value === o.id}
                 onChange={() => onChange(o.id)}
                 style={{ accentColor: C.cinnabar, marginTop: 3 }} />
          <div>
            <div style={{ fontFamily: "var(--plex)", fontSize: 12.5,
                          color: value === o.id ? C.cinnabar : C.bone, fontWeight: 500 }}>
              {o.label}
            </div>
            {o.sub && <div style={{ fontFamily: "var(--plex)", fontStyle: "italic",
                                     color: C.linen, fontSize: 11.5, fontWeight: 300, marginTop: 2 }}>
              {o.sub}
            </div>}
          </div>
        </label>
      ))}
    </div>
  );

  return (
    <Collapsible title={t("section_postpass")} testId="section-postpass"
                 description={t("section_postpass_desc")}
                 badge={enabled ? "ON" : t("section_badge_pro")}
                 badgeColor={enabled ? C.brass : C.dust}>
      {/* Toggle */}
      <ToggleRow label={t("postpass_toggle")} on={enabled}
                 onToggle={(v) => setStrategy({ ...strategy, postPassEnabled: v })}
                 testId="pp-toggle">
        <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                      fontSize: 11.5, marginTop: 4 }}>
          › {t("postpass_toggle_hint")}
        </div>
      </ToggleRow>

      {enabled && (
        <>
          <div style={{ borderTop: `1px dotted ${C.haze}`, margin: "12px 0" }} />

          {/* Horizon slider */}
          <div style={{ marginBottom: 12 }}>
            <div className="fg-label" style={{ marginBottom: 6 }}>
              {t("postpass_horizon")} · <span style={{ color: C.brass }}>{horizon}m</span>
            </div>
            <input type="range" min={1} max={24} step={1} value={horizon}
                   onChange={(e) => setStrategy({ ...strategy, postPassHorizonMonths: parseInt(e.target.value, 10) })}
                   data-testid="pp-horizon"
                   style={{ width: "100%", accentColor: C.brass }} />
            <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                          fontSize: 11, marginTop: 3 }}>
              › {t("postpass_horizon_hint")} ({horizon * 21} trading days)
            </div>
          </div>

          <div style={{ borderTop: `1px dotted ${C.haze}`, margin: "12px 0" }} />

          {/* Size mode */}
          <div className="fg-label" style={{ marginBottom: 8 }}>{t("postpass_size_title")}</div>
          <Radio name="pp-size" testPrefix="pp-size"
                 value={sizeMode}
                 onChange={v => setStrategy({ ...strategy, postPassSizeMode: v })}
                 options={[
                   { id: "same",    label: t("postpass_size_same"),    sub: t("postpass_size_same_sub") },
                   { id: "reduced", label: t("postpass_size_reduced"), sub: t("postpass_size_reduced_sub") },
                 ]} />
          {sizeMode === "reduced" && (
            <div style={{ marginBottom: 12 }}>
              <div className="fg-label" style={{ marginBottom: 6 }}>
                {t("postpass_size_factor")} · <span style={{ color: C.brass }}>{Math.round(sizeFactor * 100)}%</span>
              </div>
              <input type="range" min={0.05} max={1.0} step={0.05} value={sizeFactor}
                     onChange={(e) => setStrategy({ ...strategy, postPassSizeFactor: parseFloat(e.target.value) })}
                     data-testid="pp-size-factor"
                     style={{ width: "100%", accentColor: C.brass }} />
            </div>
          )}

          <div style={{ borderTop: `1px dotted ${C.haze}`, margin: "12px 0" }} />

          {/* Rules overrides */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        marginBottom: 8 }}>
            <div className="fg-label">{t("postpass_rules_title")}</div>
            <button className="fg-btn-ghost" onClick={resetDefaults} data-testid="pp-reset"
                    style={{ fontSize: 10.5, padding: "3px 8px" }}>
              ⟳ {t("postpass_reset")}
            </button>
          </div>
          <div style={{ fontFamily: "var(--plex)", fontStyle: "italic", color: C.linen,
                        fontSize: 11.5, marginBottom: 10 }}>
            › {t("postpass_rules_hint")}
          </div>

          {effective && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
              <NumField label={t("pp_firstPayoutMinDays")} value={ov.firstPayoutMinDays ?? effective.firstPayoutMinDays}
                        onChange={v => setOv({ firstPayoutMinDays: v })} testId="pp-firstdays" width={90} />
              <NumField label={t("pp_payoutFrequency")}    value={ov.payoutFrequency    ?? effective.payoutFrequency}
                        onChange={v => setOv({ payoutFrequency: v })}    testId="pp-freq" width={90} />
              <NumField label={t("pp_payoutBuffer")}  prefix="$" value={ov.payoutBuffer ?? effective.payoutBuffer}
                        onChange={v => setOv({ payoutBuffer: v })} testId="pp-buffer" width={90} />
              <NumField label={t("pp_payoutMinAmount")} prefix="$" value={ov.payoutMinAmount ?? effective.payoutMinAmount}
                        onChange={v => setOv({ payoutMinAmount: v })} testId="pp-min" width={90} />
              <NumField label={t("pp_payoutMaxPct")}  step={0.05} value={ov.payoutMaxPct ?? effective.payoutMaxPct}
                        onChange={v => setOv({ payoutMaxPct: v })} testId="pp-maxpct" width={90} />
              <NumField label={t("pp_payoutMaxCap")}  prefix="$" value={ov.payoutMaxCap ?? effective.payoutMaxCap ?? null}
                        onChange={v => setOv({ payoutMaxCap: v })} testId="pp-cap" width={90} />
              <NumField label={t("pp_safetyNet")}     prefix="$" value={ov.safetyNet ?? effective.safetyNet}
                        onChange={v => setOv({ safetyNet: v })} testId="pp-safety" width={90} />
              <NumField label={t("pp_payoutConsistency")} step={0.05}
                        value={ov.payoutConsistency ?? effective.payoutConsistency ?? null}
                        onChange={v => setOv({ payoutConsistency: v })} testId="pp-cons" width={90} />
              <SelectField label={t("pp_payoutConsistencyType")}
                           value={ov.payoutConsistencyType ?? effective.payoutConsistencyType ?? ""}
                           onChange={v => setOv({ payoutConsistencyType: v })} testId="pp-cons-type"
                           options={[{ value: "", label: "—" }, { value: "vs_total", label: "vs_total" }]} />
              <SelectField label={t("pp_ddTypeOverride")}
                           value={ov.ddTypeOverride ?? effective.ddType}
                           onChange={v => setOv({ ddTypeOverride: v })} testId="pp-ddtype"
                           options={[
                             { value: "static",             label: "static" },
                             { value: "trailing_eod",       label: "trailing_eod" },
                             { value: "trailing_intraday",  label: "trailing_intraday" },
                           ]} />
              <NumField label={t("pp_ddValueOverride")} prefix="$" value={ov.ddValueOverride ?? effective.ddValue}
                        onChange={v => setOv({ ddValueOverride: v })} testId="pp-ddvalue" width={90} />
              <SelectField label={t("pp_floorLockOverride")}
                           value={ov.floorLockOverride ?? effective.floorLock ?? "none"}
                           onChange={v => setOv({ floorLockOverride: v })} testId="pp-floorlock"
                           options={[
                             { value: "none",                 label: "none" },
                             { value: "at_capital",           label: "at_capital" },
                             { value: "at_target_level",      label: "at_target_level" },
                             { value: "at_capital_plus_100",  label: "at_capital_plus_100" },
                           ]} />
              <NumField label={t("pp_dailyLossOverride")} prefix="$" value={ov.dailyLossOverride ?? effective.dailyLoss ?? null}
                        onChange={v => setOv({ dailyLossOverride: v })} testId="pp-dll" width={90} />
              <ToggleRow label={t("pp_resetOnPayout")}
                         on={ov.resetOnPayout ?? effective.resetOnPayout}
                         onToggle={v => setOv({ resetOnPayout: v })}
                         testId="pp-reset-balance" />
              <ToggleRow label={t("pp_resetFloorOnPayout")}
                         on={ov.resetFloorOnPayout ?? effective.resetFloorOnPayout}
                         onToggle={v => setOv({ resetFloorOnPayout: v })}
                         testId="pp-reset-floor" />
            </div>
          )}
        </>
      )}
    </Collapsible>
  );
}
